/* eslint-disable no-mixed-spaces-and-tabs */
import * as React from "react";
import styled from "styled-components";

import Skeleton from "@ingka/skeleton";
import InlineMessage from "@ingka/inline-message";
import { colourStaticGrey, colourSemanticInformative } from "@ingka/variables/colours-css";
import Button from "@ingka/button";
import Toast from "@ingka/toast";
import rectangleHorizontalThinTriple from "@ingka/ssr-icon/paths/rectangle-horizontal-thin-tripple";
import arrowDownToBase from "@ingka/ssr-icon/paths/arrow-down-to-base";

import { Tooltip } from "@mui/material";

import { SortAscending, SortDescending } from "../../assets/icons";
import { Application } from "../../store";

import {
	ColumnInstance,
	HeaderGroup,
	Row,
	TableBodyPropGetter,
	TableBodyProps,
	TablePropGetter,
	TableProps,
} from "react-table";

import FilterColumnsModal from "./FilterColumnsModal/FilterColumnsModal";
import { TableWrapper } from "../styles";
import DashboardTile from "../DashboardTile/DashboardTile";
import LoadingAnnouncer from "../LoadingAnnouncer";
import useLoadingAnnouncer from "../LoadingAnnouncer/useLoadingAnnouncer";
import { generateElementId } from "./utils";

interface FeaturesTableProps {
	loading: boolean;
	columnFilterModal: boolean;
	setColumnFilterModal: (value: boolean) => void;
	getTableProps: (propGetter?: TablePropGetter<object> | undefined) => TableProps;
	headerGroups: HeaderGroup<object>[];
	getTableBodyProps: (propGetter?: TableBodyPropGetter<object> | undefined) => TableBodyProps;
	rows: Row<object>[];
	prepareRow: (row: Row<object>) => void;
	allColumns: ColumnInstance<object>[];
	activeApplication: Application;
	allColumnsGroup: any;
	hasGroups: boolean;
	exportGlobalDashboardTable: () => void;
	toastMessage: string;
	setToastMessage: (value: string) => void;
}

interface ColumnTitleWrapperProps {
	isSorted: boolean;
}

const ColumnTitleWrapper = styled.div<ColumnTitleWrapperProps>`
	font-weight: 700;
	width: 100%;
	${({ isSorted }) =>
		isSorted &&
		`white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;`}
`;

interface ColumnContainerProps {
	isSorted: boolean;
}

const TableHead = styled.th`
	&:not(:first-child) {
		width: 5rem;
		min-width: 5rem;
		max-width: 5rem;
	}
`;

const ColumnContainer = styled.div<ColumnContainerProps>`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const IconWrapper = styled.div<ColumnContainerProps>`
	display: flex;
	margin-left: 0.3rem;
	align-items: center;
	opacity: 0;
	transition: opacity 0.2s ease;

	${ColumnContainer}:hover &,
  	${ColumnContainer}:focus-within & {
		opacity: 1;
	}
`;

interface TableCellProps {
	cellId?: string;
	as?: "td" | "th";
}

const StyledTable = styled.table``;

const TableCell = styled.td<TableCellProps>`
	min-height: 3rem;
	text-align: ${({ as }) => (as === "th" ? "left!important" : "center")};
	align-items: center;
	justify-content: ${({ as }) => (as === "th" ? "flex-start" : "center")};
`;

const TableToolbarWrapper = styled.div`
	display: flex;
	border-top: 1px solid ${colourStaticGrey};
	border-left: 1px solid ${colourStaticGrey};
	border-right: 1px solid ${colourStaticGrey};
	padding: 0.5rem;
`;

const ToolbarOptionsWrapper = styled.div`
	margin-left: auto;
`;

const SkeletonWrapper = styled.div`
	padding: 1.5rem 0;
`;

const NoRowWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 20rem;
`;

const ToggleColumnsButton = styled(Button)`
	svg:first-child {
		transform: rotate(90deg);
	}
`;

const StyledTableWrapper = styled(TableWrapper)`
	${({ bodyHeight }) =>
		bodyHeight ? `max-height: ${bodyHeight + 18}px; height: calc(100vh - 17rem);` : "min-height: 20rem;"}
`;

const HeaderWrapper = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 1rem;
`;

const Layout: React.FC<FeaturesTableProps> = ({
	loading,
	columnFilterModal,
	setColumnFilterModal,
	getTableProps,
	headerGroups,
	getTableBodyProps,
	rows,
	prepareRow,
	allColumns,
	activeApplication,
	allColumnsGroup,
	hasGroups,
	exportGlobalDashboardTable,
	toastMessage,
	setToastMessage,
}) => {
	const tableRef = React.useRef<any>(null);
	const [tableHeight, setTableHeight] = React.useState<string>("");

	React.useEffect(() => {
		tableRef?.current?.clientHeight ? setTableHeight(tableRef.current.clientHeight) : setTableHeight("320");
	}, [rows]);

	const getSortingMeta = React.useCallback((isSorted: boolean, isSortedDesc: boolean) => {
		const sortingValue = isSorted ? (isSortedDesc ? "descending" : "ascending") : "none";

		if (sortingValue === "descending") {
			return {
				isSorted,
				ariaSort: sortingValue,
				nextSort: "ascending",
				sortIcon: <SortDescending height={16} width={16} colour={colourSemanticInformative} />,
			};
		}
		if (sortingValue === "ascending") {
			return {
				isSorted,
				ariaSort: sortingValue,
				nextSort: "descending",
				sortIcon: <SortAscending height={16} width={16} colour={colourSemanticInformative} />,
			};
		}
		return {
			isSorted: false,
			ariaSort: sortingValue,
			nextSort: "descending",
			sortIcon: <SortDescending height={16} width={16} />,
		};
	}, []);

	useLoadingAnnouncer("globalDashboard", loading);

	return (
		<>
			<LoadingAnnouncer loadingMessage="Loading dashboard" completedMessage="Loading completed" />
			<HeaderWrapper>
				<h1>{activeApplication ? `${activeApplication.name} - Global Overview` : "Global Overview"}</h1>
				<DashboardTile showGlobal={true} />
			</HeaderWrapper>

			{loading && (
				<SkeletonWrapper>
					<Skeleton height="2rem" width="34rem" />
				</SkeletonWrapper>
			)}
			<TableToolbarWrapper>
				<ToolbarOptionsWrapper>
					<ToggleColumnsButton
						type="tertiary"
						text="Toggle Columns"
						size="small"
						disabled={loading || !allColumns.length}
						ssrIcon={rectangleHorizontalThinTriple}
						onClick={() => setColumnFilterModal(true)}
					/>
					<Button
						type="tertiary"
						text="Export"
						size="small"
						disabled={loading || !allColumns.length}
						ssrIcon={arrowDownToBase}
						onClick={() => exportGlobalDashboardTable()}
					/>
				</ToolbarOptionsWrapper>
			</TableToolbarWrapper>
			<StyledTableWrapper bodyHeight={tableHeight}>
				{!allColumns.length && (
					<NoRowWrapper>
						<InlineMessage
							title="No Rows"
							body={"Seems like you don't have dashboard data yet..."}
							variant="cautionary"
							headingLevel="2"
						/>
					</NoRowWrapper>
				)}
				<StyledTable ref={tableRef} className="table" {...getTableProps()}>
					<thead>
						{headerGroups.map((headerGroup: any) => {
							const { key: headerGroupKey, ...restOfHeaderGroupProps } = headerGroup.getHeaderGroupProps();
							return (
								<tr className="table-row" key={headerGroupKey} {...restOfHeaderGroupProps}>
									{headerGroup.headers.map((column: any, columnIndex: number) => {
										const { isSorted, sortIcon, ariaSort, nextSort } = getSortingMeta(
											column.isSorted,
											column.isSortedDesc
										);

										const tooltipTitle = column.description ?? (typeof column.Header === "string" ? column.Header : "");
										return (
											<TableHead
												className="table-head"
												{...column.getHeaderProps()}
												aria-sort={ariaSort}
												id={generateElementId(column?.Header)}
												key={columnIndex}
												headers={generateElementId(column?.parent?.id)}
											>
												<ColumnContainer isSorted={isSorted}>
													<Tooltip
														slotProps={{
															tooltip: {
																sx: { bgcolor: "black" },
															},
														}}
														title={tooltipTitle}
													>
														<ColumnTitleWrapper className="title-wrapper" isSorted={isSorted}>
															{column.render("Header")}
														</ColumnTitleWrapper>
													</Tooltip>
													{column.canSort && (
														<IconWrapper
															role="button"
															tabIndex={0}
															isSorted={isSorted}
															className="icons-wrapper"
															{...column.getSortByToggleProps({ title: undefined })}
															aria-label={`Sort ${
																column.id === "markets" ? "markets" : column.render("Header")
															} by ${nextSort}`}
														>
															{sortIcon}
														</IconWrapper>
													)}
												</ColumnContainer>
											</TableHead>
										);
									})}
								</tr>
							);
						})}
					</thead>
					<tbody {...getTableBodyProps()}>
						{rows.map((row: any) => {
							prepareRow(row);
							const { key: rowKey, ...restOfRowProps } = row.getRowProps();
							return (
								<tr className="table-row" key={rowKey} {...restOfRowProps}>
									{row.cells.map((cell: any, cellIndex: number) => {
										const isRowHeader = cell.column.id === "markets";
										const headers = isRowHeader
											? undefined
											: `${generateElementId(cell.row?.values?.markets)} ${generateElementId(
													cell.column?.Header
											  )} ${generateElementId(cell.column?.parent?.id)}`;
										const { key: cellKey, ...restOftheCellProps } = cell.getCellProps();
										return (
											<TableCell
												as={isRowHeader ? "th" : "td"}
												key={cellKey}
												cellId={cell.column.id}
												className="table-cell"
												{...restOftheCellProps}
												role={isRowHeader ? "rowheader" : "cell"}
												id={isRowHeader ? generateElementId(cell?.value) : undefined}
												headers={headers}
											>
												{cell.render("Cell")}
											</TableCell>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</StyledTable>
			</StyledTableWrapper>
			<FilterColumnsModal
				columnFilterModal={columnFilterModal}
				setColumnFilterModal={setColumnFilterModal}
				allColumns={allColumns}
				allColumnsGroup={allColumnsGroup}
				hasGroups={hasGroups}
			/>
			<Toast
				data-testid="message"
				text={toastMessage}
				isOpen={!!toastMessage}
				onCloseRequest={() => setToastMessage("")}
				ariaLabelCloseBtn="Dismiss notification"
			/>
		</>
	);
};

export default Layout;
